import { useUserConfigurations } from "@via/api/configuration";
import { PageHeading } from "@via/components/page-heading.tsx";
import { statusOf } from "@via/pages/apps/hpc/hpc-apps.api.tsx";
import { previewRefusalText } from "@via/pages/agent-workspaces/job-request-review.tsx";
import { MountsEditor } from "@via/pages/agent-workspaces/mounts-editor.tsx";
import {
  CHAIN_DISPATCH_REFUSED_TEXT,
  CHAIN_SKIP_REASON_TEXT,
  JOB_STATUS_TEXT,
  NOT_YOURS_TEXT,
  NO_COMMAND_TEXT,
  cancelErrorText,
  commandText,
  isCancellable,
  useCancelJob,
  useMarkWorkspaceSeen,
  useWorkspace,
  useWorkspaceJobs,
} from "@via/pages/agent-workspaces/workspaces.api.tsx";
import { ROUTES } from "@via/utils/constants.tsx";
import { type WorkspaceJobView } from "common";
import { type JSX, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

const secondaryButton =
  "rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50";
const dangerButton =
  "rounded-md bg-red-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-50";

/**
 * A workspace's jobs, newest first, polling while any may still change. Review opens the approval
 * screen of a job that waits for one; Cancel asks first and is offered only on a job the scheduler
 * holds. The poller records how a cancelled job ended, so this list never marks one itself.
 */
export function WorkspaceJobs({
  workspaceId,
}: {
  workspaceId: string;
}): JSX.Element {
  const jobs = useWorkspaceJobs(workspaceId);
  const cancel = useCancelJob(workspaceId);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const onCancel = async (submissionKey: string): Promise<void> => {
    setConfirming(null);
    setError(null);
    setNotice(null);
    try {
      await cancel.mutateAsync(submissionKey);
      setNotice(
        "The cluster took the cancel. The list shows how the job ended."
      );
    } catch (failure) {
      setError(cancelErrorText(failure));
    }
  };

  const rows: WorkspaceJobView[] = jobs.data ?? [];

  return (
    <section className="space-y-3" aria-labelledby="workspace-jobs-heading">
      <h2
        id="workspace-jobs-heading"
        className="text-base font-semibold text-gray-900">
        Jobs
      </h2>
      {error ? (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}
      {notice ? (
        <p className="text-sm text-gray-700" role="status">
          {notice}
        </p>
      ) : null}
      {jobs.isLoading ? (
        <p className="text-sm text-gray-600">Loading jobs</p>
      ) : jobs.isError ? (
        <p className="text-sm text-gray-600">
          This workspace&apos;s jobs could not be loaded.
        </p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-600">No jobs yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead>
              <tr className="text-left text-gray-900">
                <th className="px-3 py-2 font-semibold">Command</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold">Job id</th>
                <th className="px-3 py-2 font-semibold">Resources</th>
                <th className="px-3 py-2 font-semibold">Created</th>
                <th className="px-3 py-2 font-semibold">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-gray-700">
              {rows.map(job => {
                const command = commandText(job.command);
                return (
                  <tr
                    key={job.submissionKey}
                    data-testid={`workspace-job-${job.submissionKey}`}>
                    <td className="max-w-md px-3 py-2">
                      {command === NO_COMMAND_TEXT ? (
                        <span className="italic text-gray-500">{command}</span>
                      ) : (
                        <span
                          className="block truncate font-mono"
                          title={command}>
                          {command}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {JOB_STATUS_TEXT[job.status]}
                      {job.status === "refused" && job.refusal ? (
                        <span className="block text-xs text-gray-600">
                          {`${previewRefusalText(job.refusal)} Code: `}
                          <span className="font-mono">{job.refusal}</span>
                        </span>
                      ) : null}
                      {job.chain && job.status === "skipped" ? (
                        <span className="block text-xs text-gray-600">
                          {job.chain.reason
                            ? CHAIN_SKIP_REASON_TEXT[job.chain.reason]
                            : null}
                          {job.chain.skippedBy ? (
                            <>
                              {" "}
                              Skipped because of{" "}
                              <span className="font-mono">
                                {job.chain.skippedBy}
                              </span>
                              .
                            </>
                          ) : null}
                        </span>
                      ) : null}
                      {job.chain && job.status === "dispatch_refused" ? (
                        <span className="block text-xs text-gray-600">
                          {CHAIN_DISPATCH_REFUSED_TEXT}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 font-mono">
                      {job.schedulerJobId ?? ""}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      {`${job.cpus} cpus, ${job.memoryMb} MB, ${job.wallMinutes} min`}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      {new Date(job.createdAt).toLocaleString()}
                    </td>
                    <td className="space-x-2 whitespace-nowrap px-3 py-2 text-right">
                      {job.status === "awaiting_approval" &&
                      job.requestId !== null ? (
                        <Link
                          className="font-semibold text-indigo-600 hover:text-indigo-500"
                          to={`${ROUTES.WORKSPACES}/${workspaceId}/requests/${job.requestId}`}>
                          Review
                        </Link>
                      ) : null}
                      {isCancellable(job) &&
                      confirming !== job.submissionKey ? (
                        <button
                          type="button"
                          className={secondaryButton}
                          disabled={cancel.isPending}
                          onClick={() => setConfirming(job.submissionKey)}>
                          Cancel
                        </button>
                      ) : null}
                      {isCancellable(job) &&
                      confirming === job.submissionKey ? (
                        <span
                          role="alertdialog"
                          aria-label="Confirm cancel"
                          className="inline-flex items-center gap-2">
                          <span>Cancel this job? The cluster stops it.</span>
                          <button
                            type="button"
                            className={dangerButton}
                            onClick={() => onCancel(job.submissionKey)}>
                            Yes, cancel it
                          </button>
                          <button
                            type="button"
                            className={secondaryButton}
                            onClick={() => setConfirming(null)}>
                            Keep it running
                          </button>
                        </span>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const STATE_TEXT = {
  provisioning: "Being created",
  ready: "Ready",
  archived: "Archived",
  destroyed: "Removed",
} as const;

/** One workspace: what it is, its mounts and its jobs (agent-7-4, agent-d73). */
export default function WorkspaceDetail(): JSX.Element | null {
  const { workspaceId } = useParams();
  const { data: configurations } = useUserConfigurations();
  const enabled = Boolean(configurations?.AGENT_WORKSPACE_JOBS_ENABLED);
  const workspace = useWorkspace(workspaceId, enabled);
  const { mutate: markSeen } = useMarkWorkspaceSeen();
  const seenFor = useRef<string | null>(null);

  // Opening the page clears the skipped steps from the header badge (spec D8), once per workspace.
  // Only a workspace that loaded is posted for, so another person's reads as not found and posts nothing.
  useEffect(() => {
    if (!workspace.isSuccess || !workspaceId || seenFor.current === workspaceId)
      return;
    seenFor.current = workspaceId;
    markSeen(workspaceId);
  }, [workspace.isSuccess, workspaceId, markSeen]);

  if (!enabled || !workspaceId) return null;

  const back = (
    <Link
      className="text-sm font-semibold text-indigo-600 hover:text-indigo-500"
      to={ROUTES.WORKSPACES}>
      All workspaces
    </Link>
  );

  if (workspace.isLoading) {
    return <p className="p-4 text-sm text-gray-600">Loading the workspace</p>;
  }
  if (workspace.isError || !workspace.data) {
    return (
      <main className="space-y-3 p-4">
        <p className="text-sm text-gray-600">
          {statusOf(workspace.error) === 404
            ? NOT_YOURS_TEXT
            : "This workspace could not be loaded."}
        </p>
        {back}
      </main>
    );
  }

  const data = workspace.data;
  return (
    <main className="space-y-6 p-4">
      <PageHeading title={data.name} />
      {back}
      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[12rem_1fr]">
        <dt className="font-semibold text-gray-900">State</dt>
        <dd className="text-gray-700">{STATE_TEXT[data.state]}</dd>
        <dt className="font-semibold text-gray-900">Image</dt>
        <dd className="break-all font-mono text-gray-700">{data.image}</dd>
        {data.root ? (
          <>
            <dt className="font-semibold text-gray-900">Folder</dt>
            <dd className="break-all font-mono text-gray-700">{data.root}</dd>
          </>
        ) : null}
        {data.failureReason ? (
          <>
            <dt className="font-semibold text-gray-900">Problem</dt>
            <dd className="text-red-600">{data.failureReason}</dd>
          </>
        ) : null}
      </dl>
      <MountsEditor
        workspaceId={workspaceId}
        profileClusterId={data.profileClusterId}
      />
      <WorkspaceJobs workspaceId={workspaceId} />
    </main>
  );
}
